import React from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './TrialStatusCard.module.css';

interface TrialStatusCardProps {
  isOnTrial: boolean;
  trialDaysRemaining?: number;
  trialEndDate?: string | null;
  paymentStatus?: string;
  paidUntil?: string | null;
}

const TrialStatusCard: React.FC<TrialStatusCardProps> = ({
  isOnTrial,
  trialDaysRemaining = 0,
  trialEndDate,
  paymentStatus,
  paidUntil
}) => {
  const navigate = useNavigate();

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getStatusBadge = () => {
    if (isOnTrial) {
      if (trialDaysRemaining <= 1) {
        return <span className={`${styles.badge} ${styles.expiringSoon}`}>Trial Ending Soon</span>;
      }
      return <span className={`${styles.badge} ${styles.trial}`}>Free Trial</span>;
    }
    
    if (paymentStatus === 'active') {
      return <span className={`${styles.badge} ${styles.active}`}>Active Subscription</span>;
    }
    
    if (paymentStatus === 'expired') {
      return <span className={`${styles.badge} ${styles.expired}`}>Expired</span>;
    }

    return <span className={`${styles.badge} ${styles.pending}`}>Pending</span>;
  };

  const getProgressPercentage = () => {
    if (!isOnTrial) return 100;
    const totalDays = 7;
    const daysUsed = totalDays - trialDaysRemaining;
    return (daysUsed / totalDays) * 100;
  };

  const getProgressColor = () => {
    const percentage = getProgressPercentage();
    if (percentage >= 85) return '#ef4444'; // Red
    if (percentage >= 60) return '#f59e0b'; // Orange
    return '#10b981'; // Green
  };

  return (
    <div className={styles.trialCard}>
      <div className={styles.header}>
        <h3>Subscription Status</h3>
        {getStatusBadge()}
      </div>

      {isOnTrial ? (
        <div className={styles.trialInfo}>
          <div className={styles.leftSection}>
            <div className={styles.daysRemaining}>
              <span className={styles.number}>{trialDaysRemaining}</span>
              <span className={styles.label}>Days Remaining</span>
            </div>
          </div>

          <div className={styles.rightSection}>
            <div className={styles.dateInfo}>
              <div className={styles.dateRow}>
                <span className={styles.dateLabel}>Trial Ends:</span>
                <span className={styles.dateValue}>{formatDate(trialEndDate)}</span>
              </div>
            </div>

            <div className={styles.progressContainer}>
              <div className={styles.progressBar}>
                <div 
                  className={styles.progressFill}
                  style={{ 
                    width: `${getProgressPercentage()}%`,
                    backgroundColor: getProgressColor()
                  }}
                ></div>
              </div>
              <p className={styles.progressText}>
                {7 - trialDaysRemaining} of 7 trial days used
              </p>
            </div>

            <div className={styles.actions}>
              <button 
                className={styles.upgradeButton}
                onClick={() => navigate('/Subscription')}
              >
                🚀 Upgrade to Premium
              </button>
              <p className={styles.upgradeNote}>
                Unlock unlimited access.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className={styles.subscriptionInfo}>
            <div className={styles.infoRow}>
              <span className={styles.infoLabel}>Status:</span>
              <span className={styles.infoValue}>
                {paymentStatus === 'active' ? 'Active' : 
                 paymentStatus === 'expired' ? 'Expired' : 'Pending'}
              </span>
            </div>
            {paidUntil && (
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>
                  {paymentStatus === 'active' ? 'Renews:' : 'Expired:'}
                </span>
                <span className={styles.infoValue}>{formatDate(paidUntil)}</span>
              </div>
            )}
          </div>

          {paymentStatus === 'expired' && (
            <div className={styles.actions}>
              <button 
                className={styles.renewButton}
                onClick={() => navigate('/Subscription')}
              >
                Renew Subscription
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TrialStatusCard;
